import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService, SessionFacade, SessionRepository } from '@frontend-site/shared/core';
import { format } from 'date-fns';
import { ViewAllLicensesDialogComponent } from '../view-all-licenses-dialog/view-all-licenses-dialog.component';
import {
    PDialogFooterBtnActionType,
    PDialogFooterBtnConfig,
    PDialogService,
    PErrorDialogComponent,
    PInfoDialogComponent
} from '@frontend-site/ui/components';
import { HybridCommunicationService } from '@frontend-site/shared/hybrid';
import { Language } from '@frontend-site/shared/data';
import { Observable, tap, take } from 'rxjs';
import { JobTitleOption, MfaMethod, UserMfaState, UserProfile, UserService } from '@frontend-site/users/domain';
import { ListItem } from '@frontend-site/ui/components';
import { IconName } from '@panaya/ui/p-icon';
import { getFormattedDateIncludeTime } from '@frontend-site/shared/utils';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';

@Component({
    selector: 'p-user-profile-dialog',
    templateUrl: './user-profile-dialog.component.html',
    styleUrls: ['./user-profile-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileDialogComponent implements OnInit {
    language: Language[] = [];
    user: UserProfile = <UserProfile>{};
    companyId?: number;
    appLanguage: string | undefined;
    jobTitles: ListItem[] = [];
    private jobTitleKeyById = new Map<number, string>();
    form: UntypedFormGroup = new UntypedFormGroup({}, undefined, undefined);
    private data: any;
    user$!: Observable<UserProfile>;

    constructor(
        private readonly session: SessionFacade,
        private fb: UntypedFormBuilder,
        private dialogRef: MatDialogRef<string>,
        private translate: TranslateService,
        private sessionRepository: SessionRepository,
        private readonly UserService: UserService,
        private dialog: PDialogService,
        private readonly languageService: LanguageService,
        private readonly hybridCommunicationService: HybridCommunicationService
    ) {
        this.companyId = sessionRepository.applicationConfig?.companyId;
        this.appLanguage = sessionRepository.applicationConfig?.language;
        this.language = languageService.language;
        this.user$ = this.UserService.getUserProfile(this.companyId).pipe(
            tap((user) => {
                this.form = this.buildForm(user);
                this.user = user;
            })
        );
    }

    ngOnInit(): void {
        this.UserService.getJobTitles()
            .pipe(take(1))
            .subscribe((titles: JobTitleOption[]) => {
                titles.forEach((jt) => this.jobTitleKeyById.set(jt.id, jt.key));
                this.jobTitles = titles.map((jt) => ({
                    id: jt.id,
                    name: this.translate.instant(`JobTitle.Options.${jt.key}.Name`),
                    rightIcon: { name: 'info' as IconName, showOnlyOnHover: false },
                    tooltipText: this.translate.instant(`JobTitle.Options.${jt.key}.Description`)
                }));
            });
    }

    buildForm(user: UserProfile): UntypedFormGroup {
        return this.fb.group({
            email: [user.email],
            lastLoginTime: [user.lastLoginTime],
            nearestExpiryDate: [this.getNearestLicenseExpiry(user)],
            firstName: [user.firstName, [Validators.required]],
            lastName: [user.lastName, [Validators.required]],
            jobTitle: [user.jobTitleId],
            sapOwnerName: [user.sapOwnerName],
            language: [this.appLanguage],
            receivingMsg: [user.receivingMsg],
            receivingEmail: [user.receivingEmail],
            // Security tab: the default second factor the user picked but has not saved yet (null = unchanged)
            mfaMethod: [null]
        });
    }

    viewAllLicenses(): void {
        const companyId = this.companyId;
        const config = { panelClass: 'p-dialog-medium', data: { companyId } };
        this.dialog.open(ViewAllLicensesDialogComponent, config);
    }

    get footerConfig(): PDialogFooterBtnConfig[] {
        return [
            {
                text: 'Actions.saveChange',
                click: () => this.save(),
                disabled: this.form?.invalid
            },
            {
                text: 'Actions.clear',
                type: PDialogFooterBtnActionType.Close
            }
        ];
    }

    // ---- Security tab ----

    get stagedDefault(): MfaMethod | null {
        return this.form?.get('mfaMethod')?.value ?? null;
    }

    onMakeDefault(method: MfaMethod): void {
        this.form.get('mfaMethod')?.setValue(method);
    }

    /** Enrollment / removal committed on the server: a staged default that is gone can no longer be saved. */
    onMfaStateChange(state: UserMfaState): void {
        const staged = this.stagedDefault;
        if (staged != null && !state.enrolledMethods.includes(staged)) {
            this.form.get('mfaMethod')?.setValue(null);
        }
    }

    onChangePassword(): void {
        // Password commits immediately inside the dialog (POST /UM/password/change). Nothing to stage here.
        this.dialog.open(ChangePasswordDialogComponent, { panelClass: 'p-dialog-small' });
    }

    private getNearestLicenseExpiry(user: UserProfile): string {
        return (
            this.formatUTCDate(user.userNearestLicenseExpiryBean.nearestExpiryDate) +
            '-' +
            user.userNearestLicenseExpiryBean.subscriptionType +
            ' on ' +
            user.userNearestLicenseExpiryBean.name
        );
    }

    private formatUTCDate(date: string | number | Date): string | null {
        return date ? format(new Date(date), 'd-MMM-yyyy') : null;
    }

    format(date: string): string | undefined {
        return getFormattedDateIncludeTime(new Date(date));
    }

    save(): void {
        const { language } = this.form.value;
        if (language !== this.appLanguage) {
            this.switchLanguage(language);
        }
        this.changeProfile();
    }

    switchLanguage(language: string): void {
        this.translate.use(language);
        this.hybridCommunicationService.sendMessage('Change Language', { language }, true);
    }

    buildSavePayload(): UserProfile & { companyId?: number } {
        const userProfile = { ...this.form.value };
        userProfile.language = this.languageService.getId(this.form?.get('language')?.value);
        userProfile.jobTitleId = this.form?.get('jobTitle')?.value;
        userProfile.companyId = this.companyId;
        userProfile.mfaMethod = this.stagedDefault;
        return userProfile;
    }

    changeProfile(): void {
        this.UserService.changeProfile(this.buildSavePayload()).subscribe((res) => {
            this.data = res;
            this.dialogRef.close();
            if (this.data.result && this.data.result === 'done') {
                const { firstName, lastName, language } = this.form.value;
                const jobTitleId = this.form?.get('jobTitle')?.value;
                const jobTitleKey = jobTitleId ? this.jobTitleKeyById.get(jobTitleId) : undefined;
                this.sessionRepository.updateUserProfile({ language: language, user: { firstName, lastName, jobTitleKey } });
                this.dialog.open(PInfoDialogComponent, {
                    panelClass: 'p-dialog-small',
                    data: {
                        headerText: this.translate.instant('SystemSetup.userMng.success'),
                        bodyText: this.translate.instant('UserProfile.saveSuccess')
                    }
                });
            } else {
                this.dialog.open(PErrorDialogComponent, {
                    panelClass: 'p-dialog-small',
                    data: {
                        headerText: this.translate.instant('Messages.error_msg_header'),
                        bodyText: this.data.result
                    }
                });
            }
        });
    }
}
